// Helper to safely format timestamps
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  // Check if it's a Firebase timestamp (has seconds and nanoseconds)
  if (timestamp.seconds && timestamp.nanoseconds) {
    return new Date(timestamp.seconds * 1000).toLocaleString();
  }
  
  // Check if it's an ISO string
  if (typeof timestamp === 'string') {
    try {
      return new Date(timestamp).toLocaleString();
    } catch (e) {
      return timestamp;
    }
  }
  
  // For any other object that can't be rendered
  if (typeof timestamp === 'object') {
    return JSON.stringify(timestamp);
  }
  
  return String(timestamp);
};

// Helper to safely render any value that might be a timestamp or object
export const SafeRender = ({ value }) => {
  if (value === null || value === undefined) return 'N/A';
  
  if (typeof value === 'object' && value !== null) {
    // Check if it's a Firebase timestamp
    if (value.seconds && value.nanoseconds) {
      return formatTimestamp(value);
    }
    
    // Handle Firebase timestamps
    if (value.seconds && value.nanoseconds) {
      // Format as date only (without time)
      return new Date(value.seconds * 1000).toLocaleDateString();
    }
    
    // Don't try to render objects directly
    return JSON.stringify(value);
  }
  
  // Special string values
  if (value === 'Never' || value === 'Unknown') return value;
  
  // Try to see if the string might be a date
  if (typeof value === 'string' && (
    value.includes('/') || 
    value.includes('-') || 
    value.includes(':')
  )) {
    try {
      const date = new Date(value);
      // Check if this is actually a valid date
      if (!isNaN(date.getTime())) {
        // Format as date only (without time)
        return date.toLocaleDateString();
      }
    } catch (e) {
      // If parsing fails, just return the original string
    }
  }
  
  return String(value);
};

// Helper to check if a date is within the last 24 hours
export const isWithin24Hours = (dateValue) => {
  if (!dateValue) return false;
  
  let date;
  // Handle Firebase timestamp
  if (typeof dateValue === 'object' && dateValue.seconds) {
    date = new Date(dateValue.seconds * 1000);
  } else if (typeof dateValue === 'string') {
    try {
      date = new Date(dateValue);
    } catch (e) {
      return false;
    }
  } else {
    return false;
  }
  
  if (isNaN(date.getTime())) return false;
  
  const now = new Date();
  const hoursDiff = (now - date) / (1000 * 60 * 60);
  return hoursDiff <= 24;
};

// Function to classify CSS classes
export const classNames = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

// Function to convert URLs in text to clickable links while preserving HTML formatting
export const linkifyText = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Check if text already contains links to avoid re-processing
  if (text.includes('<a href=')) {
    return text;
  }
  
  // Simple approach: Only linkify URLs that are not already within HTML tags
  // This preserves all existing HTML formatting including lists
  const urlRegex = /(^|[^"'])(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+)($|[^"'])/gi;
  
  // Replace URLs with clickable links while preserving existing HTML
  const result = text.replace(urlRegex, (match, before, url, after) => {
    // Skip if this appears to be inside an HTML attribute
    if (before.includes('href=') || before.includes('src=')) {
      return match;
    }
    
    // Determine the full URL (add https:// for www. links)
    const fullUrl = url.startsWith('www.') ? `https://${url}` : url;
    
    // Create the link with security attributes and data attribute for event delegation
    const linkHtml = `${before}<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 underline break-words" data-external-link="true">${url}</a>${after}`;
    
    return linkHtml;
  });
  
  return result;
}; 